import { TZDate } from "@date-fns/tz";
import { addDays, addWeeks, formatISO, getISOWeek, getISOWeekYear, startOfISOWeek } from "date-fns";
import { alertDeadline, dayClosed, type NotificationKind } from "@/lib/submission/rules";

/** ISO years include the boundary days in December/January of adjacent years. */
export function submissionYearBounds(year: number) {
  const start = startOfISOWeek(new TZDate(year, 0, 4, "UTC"));
  const end = startOfISOWeek(new TZDate(year + 1, 0, 4, "UTC"));

  return {
    from: formatISO(start, { representation: "date" }),
    until: formatISO(end, { representation: "date" }),
  };
}

export function submissionCalendarYear(day: string) {
  return getISOWeekYear(new TZDate(`${day}T12:00:00Z`, "UTC"));
}

export type CalendarWeek = { number: number; dates: string[] };

/** Every ISO week of the year, newest first, future weeks included. */
export function submissionWeeks(year: number): CalendarWeek[] {
  const { from, until } = submissionYearBounds(year);
  const weeks: CalendarWeek[] = [];

  for (
    let monday = new TZDate(`${from}T12:00:00Z`, "UTC");
    formatISO(monday, { representation: "date" }) < until;
    monday = addWeeks(monday, 1)
  ) {
    weeks.push({
      number: getISOWeek(monday),
      dates: Array.from({ length: 7 }, (_, offset) =>
        formatISO(addDays(monday, offset), { representation: "date" }),
      ),
    });
  }

  return weeks.reverse();
}

export type CalendarSection =
  | { kind: "month"; month: string; weeks: CalendarWeek[] }
  | { kind: "before"; weeks: CalendarWeek[] };

/**
 * Month strips (a week belongs to its Thursday's month), newest first, then
 * the weeks that ended before tracking began folded into one section. Without
 * a recorded day, tracking has not begun: every week before the current one
 * folds, which covers a whole past year and none of a future one.
 */
export function calendarSections(
  weeks: readonly CalendarWeek[],
  firstRecorded: string | undefined,
  today: string,
): CalendarSection[] {
  const start = firstRecorded && firstRecorded < today ? firstRecorded : today;
  const sections: CalendarSection[] = [];
  const before: CalendarWeek[] = [];

  for (const week of weeks) {
    if (week.dates[6]! < start) {
      before.push(week);
      continue;
    }

    const month = week.dates[3]!.slice(0, 7);
    const last = sections.at(-1);

    if (last?.kind === "month" && last.month === month) last.weeks.push(week);
    else sections.push({ kind: "month", month, weeks: [week] });
  }

  if (before.length) sections.push({ kind: "before", weeks: before });

  return sections;
}

export type CalendarRecord = {
  date: string;
  validatedAt: string | null;
  validatorName: string | null;
  cancelled: boolean;
  holidayName: string | null;
  sentAlerts: readonly NotificationKind[];
  skippedAt?: string | null;
  skippedBy?: string | null;
  skipperName?: string | null;
  skipperImage?: string | null;
  validatedByAdmin?: boolean;
};

export const CALENDAR_STATUSES = {
  verified: "Vérifié sans rappel",
  skipped: "Ignoré par l’administration",
  first: "Premier rappel envoyé",
  escalation: "Deuxième rappel envoyé",
  missed: "Vérification manquée",
  late: "Vérifié après 10 h, sans rappel envoyé",
  holiday: "Congé",
  weekend: "Fin de semaine",
  suspended: "Suivi suspendu",
  untracked: "Aucun suivi enregistré",
  upcoming: "À venir",
  pending: "À vérifier aujourd’hui",
} as const;

export type CalendarStatus = keyof typeof CALENDAR_STATUSES;

/** How a tracked day ended; holidays, pauses and skipped days have no outcome. */
export const CALENDAR_OUTCOMES = [
  "verified",
  "late",
  "first",
  "escalation",
  "missed",
] as const satisfies readonly CalendarStatus[];

export type CalendarOutcome = (typeof CALENDAR_OUTCOMES)[number];

/** A day's outcome once it is settled: past, verified today, or closed at 17 h. */
export function calendarOutcome(
  date: string,
  today: string,
  status: CalendarStatus,
  record?: CalendarRecord,
  /** Today's checks are closed (after 17 h). */
  closed = false,
): CalendarOutcome | null {
  if (date > today || (date === today && !record?.validatedAt && !closed)) return null;

  return isOutcome(status) ? status : null;
}

function isOutcome(status: CalendarStatus): status is CalendarOutcome {
  return CALENDAR_OUTCOMES.some((outcome) => outcome === status);
}

export function calendarStatus(
  date: string,
  today: string,
  record?: CalendarRecord,
  holiday?: string,
  /** Today's checks are closed (after 17 h). */
  closed = false,
): CalendarStatus {
  if (record?.skippedAt) return "skipped";

  if (!record?.validatedAt) {
    if (record?.holidayName || holiday) return "holiday";

    if (record?.cancelled) return "suspended";
    const day = new TZDate(`${date}T12:00:00Z`, "UTC").getDay();

    if (day === 0 || day === 6) return "weekend";

    if (date > today) return "upcoming";

    if (!record) return "untracked";

    if (dayClosed(date, today, closed)) return "missed";
  }

  if (record?.sentAlerts.includes("escalation")) return "escalation";

  if (record?.sentAlerts.includes("first")) return "first";

  if (record?.validatedAt)
    return new Date(record.validatedAt) < alertDeadline(date, "first") ? "verified" : "late";

  return "pending";
}
